import { ResourceFilterElement, ResourceFiltersSelectedItem } from "../../../../../shared/components/createOrder/createOrderTypes"
import { StorageKey } from "../../../../../shared/cache/LocalCache"
import type { TranslationKey } from "../../../../localization/translations"
import {
    DeliveryType,
    MeetingServiceOrder,
} from "../../../../merchant/merchant/production/meetingService/meetingServiceTypes"

import { loadSavedFilters, saveFilters } from "./filterStorage"

const STORAGE_KEY = StorageKey.SERVICE_CENTER_MEETING_SERVICE_FILTERS
const MULTISELECT = "MultiSelect"
const EXTRA_SERVICE = "EXTRA_SERVICE"

const FILTER = {
    DELIVERY_TYPE: "deliveryType",
    SET_TABLES: "setTables",
    CLEANUP: "cleanup",
} as const

const SERVICE_TYPE = {
    SET_TABLES: "TABLE_SETTING_FEE",
    CLEANUP: "CLEANUP_FEE",
} as const

const DELIVERY_TYPE_OPTIONS: ReadonlyArray<{ value: DeliveryType; nameKey: TranslationKey }> = [
    { value: DeliveryType.DELIVERY, nameKey: "delivery-type-filter-delivery" },
    { value: DeliveryType.PICK_UP, nameKey: "delivery-type-filter-pick-up" },
    { value: DeliveryType.EATING_IN_CANTEEN, nameKey: "delivery-type-filter-eating-in-canteen" },
]

type L10n = { getString: (id: TranslationKey) => string }

function hasExtraService(order: MeetingServiceOrder, serviceType: string) {
    return (order.deliveries ?? []).some((delivery) =>
        (delivery.orderLines ?? []).some((line) => line.type === EXTRA_SERVICE && line.serviceType === serviceType),
    )
}

// The available filters shown in the "Add filters" menu. The Timeline gets these from an API; we have none, so build locally.
export function buildMeetingFilterElements(l10n: L10n): ResourceFilterElement[] {
    return [
        {
            identifier: FILTER.DELIVERY_TYPE,
            elementType: MULTISELECT,
            name: FILTER.DELIVERY_TYPE,
            translationKey: "meeting-service-table-delivery-type",
            values: DELIVERY_TYPE_OPTIONS.map((option) => ({ key: l10n.getString(option.nameKey), value: option.value })),
        },
        {
            identifier: FILTER.SET_TABLES,
            elementType: MULTISELECT,
            name: FILTER.SET_TABLES,
            translationKey: "service-center-reports-dashboard-meeting-filter-set-tables",
            values: [{ key: l10n.getString("service-center-reports-dashboard-meeting-filter-set-tables"), value: FILTER.SET_TABLES }],
        },
        {
            identifier: FILTER.CLEANUP,
            elementType: MULTISELECT,
            name: FILTER.CLEANUP,
            translationKey: "service-center-reports-dashboard-meeting-filter-cleanup",
            values: [{ key: l10n.getString("service-center-reports-dashboard-meeting-filter-cleanup"), value: FILTER.CLEANUP }],
        },
    ]
}

// Same shape as the project's filterResources: filters.every() with a switch on the filter identifier.
export function matchesMeetingFilters(order: MeetingServiceOrder, filters: ReadonlyArray<ResourceFiltersSelectedItem>) {
    return filters.every((filter) => {
        const values = filter.values.map((value) => value.value)
        if (!values.length) return true
        switch (filter.identifier) {
            case FILTER.DELIVERY_TYPE:
                return values.includes(order.deliveryDetails?.type ?? "")
            case FILTER.SET_TABLES:
                return hasExtraService(order, SERVICE_TYPE.SET_TABLES)
            case FILTER.CLEANUP:
                return hasExtraService(order, SERVICE_TYPE.CLEANUP)
            default:
                return true
        }
    })
}

export const loadSavedMeetingFilters = () => loadSavedFilters(STORAGE_KEY)
export const saveMeetingFilters = (filters: ResourceFiltersSelectedItem[]) => saveFilters(STORAGE_KEY, filters)
